import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";

function _createSuper(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

import React, { PureComponent, isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ENTER } from '../constants/keys';
import { withStyles } from '../theme';
import { isolateMixin } from '../utils/mixins';
import uuid from '../utils/uuid';
import { MenuContext } from './context';

var styles = function styles(theme) {
  return _extends({
    root: {
      extend: isolateMixin,
      fontFamily: theme.fontFamily,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      paddingLeft: theme.menu.padding,
      paddingRight: theme.menu.padding,
      outline: 0,
      fontSize: theme.menu.fontSize,
      fontWeight: theme.menu.fontWeight,
      lineHeight: theme.menu.lineHeight + 'px',
      borderRadius: theme.menu.item.borderRadius,
      '&$isSelected': {
        color: theme.menu.colors.selected.text,
        backgroundColor: theme.menu.colors.selected.background,
        fontWeight: theme.menu.selectedFontWeight,
        '&:hover': {
          backgroundColor: theme.menu.colors.selected.background
        }
      }
    }
  }, ['medium', 'small'].reduce(function (result, size) {
    return _extends({}, result, _defineProperty({}, size, {
      minHeight: theme.menu.sizes[size].height,
      paddingTop: (theme.menu.sizes[size].height - theme.menu.lineHeight) / 2,
      paddingBottom: (theme.menu.sizes[size].height - theme.menu.lineHeight) / 2
    }));
  }, {}), {
    isSelected: {},
    isEnabled: {
      color: theme.menu.colors.default.text,
      backgroundColor: theme.menu.colors.default.background,
      cursor: 'pointer',
      '&:hover': {
        color: theme.menu.colors.hover.text,
        backgroundColor: theme.menu.colors.hover.background
      },
      '&:focus': {
        color: theme.menu.colors.focus.text,
        background: theme.menu.colors.focus.background
      },
      '&:active': {
        color: theme.menu.colors.active.text,
        background: theme.menu.colors.active.background
      }
    },
    isDisabled: {
      color: theme.menu.colors.disabled.text,
      background: theme.menu.colors.disabled.background,
      cursor: 'not-allowed'
    }
  });
};

var MenuItem = /*#__PURE__*/function (_PureComponent) {
  _inherits(MenuItem, _PureComponent);

  var _super = _createSuper(MenuItem);

  function MenuItem() {
    var _this;

    _classCallCheck(this, MenuItem);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.id = uuid();

    _this.handlePropsChange = function () {
      var _assertThisInitialize = _assertThisInitialized(_this),
          props = _assertThisInitialize.props,
          context = _assertThisInitialize.context;

      if (props.hasOwnProperty('value') && context.isValueSelected(props.value) !== _this.isSelected || context.isItemFocused(_this.id) !== _this.isFocused || context.isMenuDisabled() !== _this.disabled || context.getMenuSize() !== _this.size) _this.forceUpdate();
    };

    _this.handleFocus = function () {
      _this.context.events.emit('onItemFocus', _this.id);
    };

    _this.handleSelect = function () {
      var _assertThisInitialize2 = _assertThisInitialized(_this),
          props = _assertThisInitialize2.props;

      if (props.hasOwnProperty('value')) _this.context.events.emit('onItemSelect', props.value);
    };

    _this.handlePressKey = function (event) {
      if (event.keyCode === ENTER) {
        event.stopPropagation();
        event.preventDefault();

        _this.item.focus();

        _this.handleSelect();
      }
    };

    return _this;
  }

  _createClass(MenuItem, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.context.events.on('onPropsChange', this.handlePropsChange);
      this.context.events.emit('onItemMount', this.id, this);
      if (this.context.isItemFocused(this.id)) this.item.focus();
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate() {
      if (this.context.isItemFocused(this.id)) this.item.focus();
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.context.events.removeListener('onPropsChange', this.handlePropsChange);
      this.context.events.emit('onItemUnmount', this.id);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          container = _this$props.container,
          className = _this$props.className,
          value = _this$props.value,
          disabled = _this$props.disabled,
          classes = _this$props.classes,
          children = _this$props.children,
          theme = _this$props.theme,
          other = _objectWithoutProperties(_this$props, ["container", "className", "value", "disabled", "classes", "children", "theme"]);

      this.isSelected = this.props.hasOwnProperty('value') && this.context.isValueSelected(value);
      this.isFocused = this.context.isItemFocused(this.id);
      this.disabled = this.context.isMenuDisabled();
      this.size = this.context.getMenuSize();
      var isItemDisabled = !!disabled || this.disabled;
      var element;
      if (container && isValidElement(container)) element = container;else if (typeof container === 'function') element = container({
        activeClassName: classes.isSelected
      });

      var props = _extends({}, other, {
        className: classnames(className, this.context.className, classes.root, isItemDisabled ? classes.isDisabled : classes.isEnabled, this.size && classes[this.size], this.isSelected && classes.isSelected),
        tabIndex: isItemDisabled ? null : 0,
        onFocus: isItemDisabled ? null : this.handleFocus,
        onClick: isItemDisabled ? null : this.handleSelect,
        onKeyDown: isItemDisabled ? null : this.handlePressKey,
        'data-menu-item-id': this.id
      });

      return element ? cloneElement(element, props, children) : React.createElement("div", props, children);
    }
  }, {
    key: "item",
    get: function get() {
      return this.context.getItemRef(this.id);
    }
  }]);

  return MenuItem;
}(PureComponent);

MenuItem.contextType = MenuContext;
MenuItem.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Дополнительный CSS-класс
   */
  className: PropTypes.string,

  /**
   * Значение опции, по-умолчанию считается, что это примитив
   */
  value: PropTypes.any,

  /**
   * Отключение опции
   */
  disabled: PropTypes.bool,

  /**
   * Контент опции
   */
  children: PropTypes.node.isRequired,

  /**
   * Элемент, который содержит контент, например `<Link />` в случае с `react-router`.
   * Если используется `<NavLink />` с `activeClassName`,
   * нужно в `container` передавать фабрику, которая получает `activeClassName` в аргументах
   */
  container: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
} : {};
export default withStyles(styles, {
  name: 'MenuItem'
})(MenuItem);